import React from 'react';
import { Button, Tabs, Tab, Container, Grid, Box, CircularProgress, useTheme, useMediaQuery } from '@mui/material';
import Layout from '../../../components/Layout';
import { CustomTabPanel, a11yProps } from './components';
import { useSettings } from './hooks';
import {
    BasicDetailsTab,
    KioskSettingsTab,
    GateOpeningTab,
    KioskStyleTab,
    KioskUITab,
    CustomImagesTab,
    ANPRTab,
    QRCodeKeysTab,
    FaceRecognitionTab
} from './tabs';
import {
    ThemeDialog,
    BluetoothResetDialog,
    CustomKioskUIWarningDialog,
    RevertWarningDialog,
    DiffViewerDialog,
    DebugLogDialog
} from './dialogs';

const SettingsPage = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    
    const {
        // State
        settings,
        setSettings,
        loading,
        isModified,
        setIsModified,
        serverKioskUI,
        globalTenantId,
        user,
        value,
        kiosks,
        kioskTypes,
        kioskThemes,
        foundTheme,
        
        // Dialog states
        openWarningDialog,
        setOpenWarningDialog,
        openRevertWarningDialog,
        setOpenRevertWarningDialog,
        showThemeDialog,
        setShowThemeDialog,
        showResetBluetoothDialog,
        setShowResetBluetoothDialog,
        showDiffDialog,
        setShowDiffDialog,
        showLockdownAppSettingsDialog,
        setShowLockdownAppSettingsDialog,
        showMovementZonesDialog,
        setShowMovementZonesDialog,
        showANPRMovementZonesDialog,
        setShowANPRMovementZonesDialog,
        showDebugLogDialog,
        setShowDebugLogDialog,
        
        // Bluetooth states
        bluetoothResetOption,
        setBluetoothResetOption,
        bluetoothCustomPasscode,
        setBluetoothCustomPasscode,
        
        // Alias states
        disableNumberPlateAliases,
        disablePINAliases,
        disableQrCodeKeysAliases,
        disableFaceProfileAliases,
        masterPlateAliases,
        masterPINAliases,
        masterQrCodeKeyAliases,
        masterFaceProfileAliases,
        masterTenantNameForNumberPlates,
        masterTenantNameForPINs,
        masterTenantNameForQrCodeKeys,
        masterTenantNameForFaceProfiles,
        
        // Gallery and accordion states
        isGalleryOpen,
        isKioskUIGalleryOpen,
        setKioskUITextareaRef,
        kioskUICursorPosition,
        setKioskUICursorPosition,
        isAccordionExpanded,
        
        // Theme states
        customKioskThemeID,
        setCustomKioskThemeID,
        previousCustomKioskThemeID,
        setPreviousCustomKioskThemeID,
        
        // Gate schedule states
        gateSchedule,
        gateTimesDialogOpen,
        setGateTimesDialogOpen,
        
        // Debug log states
        debugLog,
        setDebugLog,
        loadingDebugLog,
        setLoadingDebugLog,
        
        // Handlers
        handleNavigationAttempt,
        handleTabChange,
        handleInputChange,
        handleScheduleChange,
        handleSave,
        handleCustomKioskUIChange,
        handleRevertToOriginal,
        confirmRevertToOriginal,
        updateKioskUI,
        handleOpenGallery,
        handleCloseGallery,
        handleImageSelect,
        handleLandscapeImageSelect,
        handleOpenKioskUIGallery,
        handleCloseKioskUIGallery,
        handleKioskUIImageSelect,
        handleAccordionToggle,
        handleCustomTheme,
        handleCustomThemeCancel,
        resetCustomTheme,
        showBlueToothDialog,
        confirmResetBluetooth,
        
        // Utilities
        axios,
    } = useSettings();

    // Return loading state if settings not yet available
    if (!settings) {
        return (
            <Layout title='Settings'>
                <CircularProgress />
            </Layout>
        );
    }

    return (
        <Layout 
            title='Settings'
            onNavigate={handleNavigationAttempt}
        >
            <Container 
                maxWidth="lg" 
                sx={{ 
                    px: { xs: 1, sm: 2, md: 3 },
                    pb: { xs: 2, sm: 3 }
                }}
            >
                <form onSubmit={handleSave}>
                    <Box sx={{ 
                        borderBottom: 1, 
                        borderColor: 'divider',
                        mx: { xs: -1, sm: 0 },
                        position: 'sticky',
                        top: 0,
                        bgcolor: 'background.paper',
                        zIndex: 10
                    }}>
                        <Tabs 
                            value={value} 
                            onChange={handleTabChange} 
                            aria-label="settings tabs" 
                            variant="scrollable"
                            scrollButtons="auto"
                            allowScrollButtonsMobile
                            sx={{
                                minHeight: { xs: 42, sm: 48 },
                                '& .MuiTab-root': {
                                    minHeight: { xs: 42, sm: 48 },
                                    minWidth: { xs: 'auto', sm: 90 },
                                    px: { xs: 1.5, sm: 2 },
                                    py: { xs: 1, sm: 1.5 },
                                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                    textTransform: 'none',
                                    fontWeight: 500
                                },
                                '& .MuiTabs-scrollButtons': {
                                    '&.Mui-disabled': {
                                        opacity: 0.3
                                    }
                                }
                            }}
                        >
                            <Tab label="Basic" {...a11yProps(0)} />
                            <Tab label="Kiosk" {...a11yProps(1)} />
                            <Tab label="Gates" {...a11yProps(2)} />
                            <Tab label="Style" {...a11yProps(3)} />
                            <Tab label="UI" {...a11yProps(4)} />
                            <Tab label="Images" {...a11yProps(5)} />
                            <Tab label="ANPR" {...a11yProps(6)} />
                            <Tab label="QR Keys" {...a11yProps(7)} />
                            <Tab label="Face ID" {...a11yProps(8)} />
                        </Tabs>
                    </Box>

                    <CustomTabPanel value={value} index={0}>
                        <BasicDetailsTab 
                            settings={settings}
                            handleInputChange={handleInputChange}
                            kioskTypes={kioskTypes}
                            updateKioskUI={updateKioskUI}
                        />
                    </CustomTabPanel>

                    <CustomTabPanel value={value} index={1}>
                        <KioskSettingsTab 
                            settings={settings}
                            handleInputChange={handleInputChange}
                            user={user}
                            globalTenantId={globalTenantId}
                            showMovementZonesDialog={showMovementZonesDialog}
                            setShowMovementZonesDialog={setShowMovementZonesDialog}
                            showLockdownAppSettingsDialog={showLockdownAppSettingsDialog}
                            setShowLockdownAppSettingsDialog={setShowLockdownAppSettingsDialog}
                            showDebugLogDialog={showDebugLogDialog}
                            setShowDebugLogDialog={setShowDebugLogDialog}
                            debugLog={debugLog}
                            setDebugLog={setDebugLog}
                            loadingDebugLog={loadingDebugLog}
                            setLoadingDebugLog={setLoadingDebugLog}
                            axios={axios}
                        />
                    </CustomTabPanel>

                    <CustomTabPanel value={value} index={2}>
                        <GateOpeningTab 
                            settings={settings}
                            handleInputChange={handleInputChange}
                            kiosks={kiosks}
                            disablePINAliases={disablePINAliases}
                            masterPINAliases={masterPINAliases}
                            masterTenantNameForPINs={masterTenantNameForPINs}
                            gateSchedule={gateSchedule}
                            handleScheduleChange={handleScheduleChange}
                            gateTimesDialogOpen={gateTimesDialogOpen}
                            setGateTimesDialogOpen={setGateTimesDialogOpen}
                            showBlueToothDialog={showBlueToothDialog}
                        />
                    </CustomTabPanel>

                    <CustomTabPanel value={value} index={3}>
                        <KioskStyleTab 
                            settings={settings}
                            handleInputChange={handleInputChange}
                            kioskThemes={kioskThemes}
                            foundTheme={foundTheme}
                            globalTenantId={globalTenantId}
                            isGalleryOpen={isGalleryOpen}
                            handleOpenGallery={handleOpenGallery}
                            handleCloseGallery={handleCloseGallery}
                            handleImageSelect={handleImageSelect}
                            handleLandscapeImageSelect={handleLandscapeImageSelect}
                            isAccordionExpanded={isAccordionExpanded}
                            handleAccordionToggle={handleAccordionToggle}
                            setPreviousCustomKioskThemeID={setPreviousCustomKioskThemeID}
                            setShowThemeDialog={setShowThemeDialog}
                            resetCustomTheme={resetCustomTheme}
                            setIsModified={setIsModified}
                        />
                    </CustomTabPanel>

                    <CustomTabPanel value={value} index={4}>
                        <KioskUITab 
                            settings={settings}
                            handleInputChange={handleInputChange}
                            handleCustomKioskUIChange={handleCustomKioskUIChange}
                            serverKioskUI={serverKioskUI}
                            handleRevertToOriginal={handleRevertToOriginal}
                            setShowDiffDialog={setShowDiffDialog}
                            globalTenantId={globalTenantId}
                            isKioskUIGalleryOpen={isKioskUIGalleryOpen}
                            handleOpenKioskUIGallery={handleOpenKioskUIGallery}
                            handleCloseKioskUIGallery={handleCloseKioskUIGallery}
                            handleKioskUIImageSelect={handleKioskUIImageSelect}
                            setKioskUITextareaRef={setKioskUITextareaRef}
                            kioskUICursorPosition={kioskUICursorPosition}
                            setKioskUICursorPosition={setKioskUICursorPosition}
                        />
                    </CustomTabPanel>

                    <CustomTabPanel value={value} index={5}>
                        <CustomImagesTab globalTenantId={globalTenantId} />
                    </CustomTabPanel>

                    <CustomTabPanel value={value} index={6}>
                        <ANPRTab 
                            settings={settings}
                            handleInputChange={handleInputChange}
                            kiosks={kiosks}
                            disableNumberPlateAliases={disableNumberPlateAliases}
                            masterPlateAliases={masterPlateAliases}
                            masterTenantNameForNumberPlates={masterTenantNameForNumberPlates}
                            showANPRMovementZonesDialog={showANPRMovementZonesDialog}
                            setShowANPRMovementZonesDialog={setShowANPRMovementZonesDialog}
                        />
                    </CustomTabPanel>

                    <CustomTabPanel value={value} index={7}>
                        <QRCodeKeysTab 
                            settings={settings}
                            handleInputChange={handleInputChange}
                            setSettings={setSettings}
                            setIsModified={setIsModified}
                            disableQrCodeKeysAliases={disableQrCodeKeysAliases}
                            masterQrCodeKeyAliases={masterQrCodeKeyAliases}
                            masterTenantNameForQrCodeKeys={masterTenantNameForQrCodeKeys}
                        />
                    </CustomTabPanel>

                    <CustomTabPanel value={value} index={8}>
                        <FaceRecognitionTab 
                            settings={settings}
                            handleInputChange={handleInputChange}
                            globalTenantId={globalTenantId}
                            disableFaceProfileAliases={disableFaceProfileAliases}
                            masterFaceProfileAliases={masterFaceProfileAliases}
                            masterTenantNameForFaceProfiles={masterTenantNameForFaceProfiles}
                        />
                    </CustomTabPanel>

                    <Box sx={{ 
                        mt: { xs: 2, sm: 3 },
                        px: { xs: 0, sm: 0 },
                        pb: { xs: 2, sm: 0 }
                    }}>
                        <Button 
                            type="submit" 
                            variant="contained" 
                            color="primary" 
                            fullWidth 
                            disabled={!isModified || loading}
                            sx={{
                                py: { xs: 1.5, sm: 1.25 },
                                fontSize: { xs: '1rem', sm: '0.95rem' },
                                fontWeight: 600,
                                borderRadius: { xs: 2, sm: 1 },
                                textTransform: 'none'
                            }}
                        >
                            {loading ? <CircularProgress size={24} color="inherit" /> : 'Save Changes'}
                        </Button>
                    </Box>
                </form>

                {/* Dialogs */}
                <CustomKioskUIWarningDialog 
                    open={openWarningDialog}
                    onClose={() => setOpenWarningDialog(false)}
                    handleInputChange={handleInputChange}
                />

                <RevertWarningDialog 
                    open={openRevertWarningDialog}
                    onClose={() => setOpenRevertWarningDialog(false)}
                    onConfirm={confirmRevertToOriginal}
                />

                <ThemeDialog 
                    open={showThemeDialog}
                    kioskThemes={kioskThemes}
                    customKioskThemeID={customKioskThemeID}
                    setCustomKioskThemeID={setCustomKioskThemeID}
                    onConfirm={handleCustomTheme}
                    onCancel={handleCustomThemeCancel}
                />

                <BluetoothResetDialog 
                    open={showResetBluetoothDialog}
                    onClose={() => setShowResetBluetoothDialog(false)}
                    settings={settings}
                    bluetoothResetOption={bluetoothResetOption}
                    setBluetoothResetOption={setBluetoothResetOption}
                    bluetoothCustomPasscode={bluetoothCustomPasscode}
                    setBluetoothCustomPasscode={setBluetoothCustomPasscode}
                    onConfirm={confirmResetBluetooth}
                />

                <DiffViewerDialog 
                    open={showDiffDialog}
                    onClose={() => setShowDiffDialog(false)}
                    serverKioskUI={serverKioskUI}
                    currentKioskUI={settings.SettingKioskUI}
                />

                <DebugLogDialog 
                    open={showDebugLogDialog}
                    onClose={() => setShowDebugLogDialog(false)}
                    debugLog={debugLog}
                    setDebugLog={setDebugLog}
                    loadingDebugLog={loadingDebugLog}
                    setLoadingDebugLog={setLoadingDebugLog}
                    globalTenantId={globalTenantId}
                />
            </Container>
        </Layout>
    );
};

export default SettingsPage;

